/**
 * Product Card Carousel
 * Initializes Swiper carousels on product cards with keyboard support
 */

class ProductCardCarousel {
  constructor() {
    this.swipers = new Map();
    // Desktop gets a crossfade (no horizontal movement, so overlays anchored to the
    // slide — badges, third-party wishlist icons — stay put) plus the hover-to-secondary
    // swap. Touch devices keep the original physical slide-follows-finger behavior.
    this.isDesktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.init();
    this.setupMutationObserver();
  }

  init() {
    const carousels = document.querySelectorAll('[data-product-carousel]');

    carousels.forEach((carousel) => {
      // Skip if already initialized
      if (this.swipers.has(carousel)) {
        return;
      }

      this.initializeSwiper(carousel);
    });
  }

  initializeSwiper(element) {
    const navStyle = this.getNavigationStyle(element);
    const paginationSelector = navStyle === 'dots' || navStyle === 'both'
      ? '.product-card__carousel-pagination'
      : false;
    const navigationSelector = navStyle === 'arrows' || navStyle === 'both'
      ? {
          nextEl: element.querySelector('.swiper-button-next'),
          prevEl: element.querySelector('.swiper-button-prev'),
        }
      : {};

    const swiper = new Swiper(element, {
      loop: true,
      effect: this.isDesktopPointer ? 'fade' : 'slide',
      fadeEffect: { crossFade: true },
      speed: this.isDesktopPointer ? 350 : 300,
      slidesPerView: 1,
      spaceBetween: 0,
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      touch: true,
      grabCursor: true,
      pagination: paginationSelector
        ? {
            el: paginationSelector,
            type: 'bullets',
            clickable: true,
          }
        : false,
      navigation: navStyle !== 'dots' ? navigationSelector : false,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
        paginationBulletMessage: 'Go to image {{index}}',
      },
    });

    this.swipers.set(element, swiper);

    // Add keyboard support for arrows when focused
    element.addEventListener('keydown', (e) => this.handleKeyboard(e, swiper));

    if (this.isDesktopPointer && element.dataset.carouselHoverSecondary === 'true') {
      this.setupHoverSecondaryImage(element, swiper);
    }

    return swiper;
  }

  /**
   * On desktop, hovering the card swaps to the second image (mirroring the
   * standard "show secondary image on hover" setting) while keeping the
   * slider nav available to manually browse the rest of the images.
   */
  setupHoverSecondaryImage(element, swiper) {
    const figure = element.closest('.product-card__figure');

    if (!figure) {
      return;
    }

    figure.addEventListener('mouseenter', () => {
      if (swiper.realIndex === 0) {
        swiper.slideToLoop(1);
      }
    });

    figure.addEventListener('mouseleave', () => {
      swiper.slideToLoop(0);
    });
  }

  getNavigationStyle(element) {
    const pagination = element.querySelector('.product-card__carousel-pagination');
    const prevBtn = element.querySelector('.swiper-button-prev');

    if (pagination && prevBtn) return 'both';
    if (pagination) return 'dots';
    if (prevBtn) return 'arrows';
    return 'arrows'; // Default fallback
  }

  handleKeyboard(event, swiper) {
    if (event.key === 'ArrowLeft') {
      swiper.slidePrev();
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      swiper.slideNext();
      event.preventDefault();
    }
  }

  /**
   * Observe DOM changes (e.g., Turbo navigation) and reinitialize carousels
   */
  setupMutationObserver() {
    // Reinit on turbo:load or when new carousels are added to DOM
    document.addEventListener('turbo:load', () => this.handleNavigation());

    // Also watch for dynamically added carousels
    const observer = new MutationObserver(() => {
      const newCarousels = document.querySelectorAll('[data-product-carousel]');
      newCarousels.forEach((carousel) => {
        if (!this.swipers.has(carousel)) {
          this.initializeSwiper(carousel);
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handleNavigation() {
    // Destroy all existing Swiper instances before reinitializing
    this.swipers.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    this.swipers.clear();

    // Reinitialize
    this.init();
  }
}

// Initialize on DOM ready and after AJAX loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductCardCarousel();
  });
} else {
  new ProductCardCarousel();
}